import { apiRequest } from '../api/request';
import { API } from '../config/envConfig';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  accountId?: string;
  prodAccountId?: string;
  userType?: string;
  profile?: string[];
  emailVerified?: boolean;
  phone?: string;
  confirmedAt?: string;
  lastSignInAt?: string;
  createdAt?: string;
  updatedAt?: string;
  // Company-specific fields (for superadmin company selection)
  companyName?: string;
  address?: string;
  businessType?: string[];
  isActivated?: boolean;
  isoLicense?: string;
  numberOfMerchants?: number | null;
  comments?: string;
  isApproved?: boolean;
  approvedBy?: string;
}

export interface LoginResponse {
  message: {
    id: string;
    email: string;
    email_confirmed_at: string;
    phone: string;
    confirmed_at: string;
    last_sign_in_at: string;
    user_metadata: {
      account_id: string;
      prod_account_id?: string;
      email_verified: boolean;
      first_name: string;
      last_name: string;
      profile?: string[];
      role: string;
      user_type: string;
    };
    access_token: string;
    created_at: string;
    updated_at: string;
  };
  status: boolean;
  restaurant_parent_id: string;
  refresh_token?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Use the login endpoint from API configuration
    const response = await fetch(API.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const loginResponse: LoginResponse = await response.json();

    // Check if login was successful
    if (!loginResponse.status) {
      // Extract the actual error message from API response
      const errorMessage = typeof loginResponse.message === 'string' 
        ? loginResponse.message 
        : 'Login failed - invalid credentials';
      throw new Error(errorMessage);
    }

    // Transform response to match expected AuthResponse format
    const user: User = {
      id: loginResponse.message.id,
      email: loginResponse.message.email,
      firstName: loginResponse.message.user_metadata.first_name,
      lastName: loginResponse.message.user_metadata.last_name,
      role: loginResponse.message.user_metadata.role,
      accountId: loginResponse.message.user_metadata.account_id,
      prodAccountId: loginResponse.message.user_metadata.prod_account_id,
      userType: loginResponse.message.user_metadata.user_type,
      profile: loginResponse.message.user_metadata.profile,
      emailVerified: loginResponse.message.user_metadata.email_verified,
      phone: loginResponse.message.phone,
      confirmedAt: loginResponse.message.confirmed_at,
      lastSignInAt: loginResponse.message.last_sign_in_at,
      createdAt: loginResponse.message.created_at,
      updatedAt: loginResponse.message.updated_at,
    };

    return {
      user,
      token: loginResponse.message.access_token,
    };
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  },

  async logout(): Promise<void> {
    await apiRequest<void>('/auth/logout', {
      method: 'POST',
    });
  },

  async refreshToken(): Promise<AuthResponse> {
    const response = await apiRequest<AuthResponse>('/auth/refresh', {
      method: 'POST',
    });
    return response;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiRequest<User>('/auth/me');
    return response;
  },

  async forgotPassword(email: string, type: string): Promise<{ status: boolean; message: string }> {
    const response = await fetch(API.FORGOT_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ email, type }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },

  async resetPassword(id: string, password: string): Promise<{ status: boolean; message: string }> {
    const response = await fetch(API.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ id, password }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },

  async updatePassword(id: string, password: string): Promise<{ status: boolean; message: string }> {
    const response = await fetch(API.UPDATE_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ id, password }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },

  async verifyOtp(email: string, code: string): Promise<{ status: boolean; message: string; data?: any; id?: string }> {
    const response = await fetch(API.VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },

  async resendOtp(email: string): Promise<{ status: boolean; message: string }> {
    const response = await fetch(API.RESEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },

  async sendOtp(email: string): Promise<{ status: boolean; message: string }> {
    const response = await fetch(API.SEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },
};
